import { animate, style, transition, trigger } from '@angular/animations';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import {
  MapGotoBlockContent,
  MapGotoBlockForm,
  MapGotoBlockTabContent,
  MapSearchBlockContent,
  MapSearchBlockForm,
  MapSearchBlockTabContent,
  MapState,
  OverallStatistics,
  ZoomOptions,
} from 'src/app/interfaces/map.interface';
import { MapFlags } from 'src/app/models/map-flags.model';
import { MapMarkers } from 'src/app/models/map-markers.model';
import { CustomMarker } from '../../models/custom-marker.model';
import { DefaultService as RestApiService } from '../../swagger-api';
import { ManageCommunityService } from '../community/manage-community.service';
import { ProfileService } from '../profile/profile.service';
import { ManageProjectService } from '../projects/manage-project.service';
import { SearchService } from '../search/search.service';
import { MapInitialFlow } from './map-flow/map-initial-flow/map-initial-flow';
import { MapSelectItemFlow } from './map-flow/map-select-item-flow/map-select-item.flow';
import { MapViewFlow } from './map-flow/map-view-flow/map-view.flow';
import { MapFlow } from './map-flow/map.flow';
import { MapItemDetailsProviderStrategy } from './map-item-details-provider/map-item-details-provider.strategy';
import { MapFilters } from './map.enum';
import { calculateOffsetDegree, hasCovidMarkers, isInNotAggregationZoomRange } from './map.helpers';
import { MarkersStateChanges } from './map.interfaces';
import { MapService } from './map.service';
import MapSettings from './map.settings';
import { DistanceCalculator } from './markers-handler-helpers/helpers/distance.calculator';
import { PinItemBindingMediator } from './markers-handler-helpers/mediators/pin-item-binding.mediator';
import { ProxyMapService } from './proxy-map.service';
import { SearchBlockComponent } from './search-block/search-block.component';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  animations: [
    trigger('searchBlockShowTrigger', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('100ms', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('300ms', style({ transform: 'translateX(100%)', opacity: 0 })),
      ]),
    ]),
  ],
  providers: [
    { provide: MapService, useClass: ProxyMapService },
    SearchService,
    ProfileService,
    ManageCommunityService,
    ManageProjectService,
  ],
})
export class MapComponent implements OnInit, OnDestroy {
  public mapFlow: MapFlow = new MapInitialFlow(this);
  public mapMarkers: MapMarkers = new MapMarkers();
  public searchBlockContent: MapSearchBlockContent;
  public goToBlockContent: MapGotoBlockContent;
  public flags: MapFlags = new MapFlags(true);

  public zoomOptions: ZoomOptions = {
    minZoom: MapSettings.MAP_MIN_ZOOM_LEVEL,
    maxZoom: MapSettings.MAP_MAX_ZOOM_LEVEL,
  };
  public mapState: MapState = null;
  public isAuthSeeMap = true;
  public isAdminSession = false;
  public mapMarker: MapGotoBlockForm;
  public mediator: PinItemBindingMediator = new PinItemBindingMediator();

  public displayedMarkers: CustomMarker[] = [];

  @ViewChild(SearchBlockComponent, { static: false }) private searchBlockComponent: SearchBlockComponent;

  private subscriptions$: Subscription[] = [];

  private mapService = inject(MapService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private apiService = inject(RestApiService);

  public readonly statistics$: Observable<OverallStatistics> = this.mapService.statisticsUnderMap;

  public ngOnInit(): void {
    this.changeMinZoomScreenSize();
    this.subscribeToSessionChanges();
    this.subscribeToMapStateChanges();
    this.subscribeToSearchBlockTabContentChanges();
    this.subscribeToMapGotoContentChanges();
    this.subscribeToMarkersChanges();
    this.subscribeOnAggregationRequestErrors();
    this.subscribeToOnClickItemAction();
    this.subscribeToRouteChanges();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  /**
   * @description Hides container with 'View map' button by switching isMapButtonVisible
   *              flag value.
   */
  public toggleButtonVisibility(): void {
    this.flags.isMapButtonVisible = false;
  }

  /**
   * @description Hides search block container button by switching isSearchBlockVisible
   *              flag value.
   */
  public toggleSearchBlockVisibility(): void {
    this.flags.isSearchBlockVisible = !this.flags.isSearchBlockVisible;
  }

  /**
   * @description Called when closeSearchBlock event was fired. Sets isSearchBlockVisible
   *              flag value as false, updates formContent data with incoming one and
   *              calls the corresponding method from map service.
   */
  public onCloseSearchBlock(data: MapSearchBlockForm): void {
    this.flags.isSearchBlockVisible = false;
    this.searchBlockContent.formContent.data = data;
  }

  /**
   * @description Called when resetForm event was fired. Calls the corresponding method
   *              from map service.
   */
  public onResetForm(): void {
    this.mapService.onResetForm();
  }

  /**
   * @description Called when formSubmitted event was fired, calls the corresponding
   *              method from map service in order to update displayed markers.
   *              Mustn't be called if user submit an empty form.
   */
  public onSearchFormSubmitted(data: MapSearchBlockForm): void {
    if (!this.flags.isSearchFormSubmitted && !this.mapService.isSearchFormSubmitted(data)) {
      return;
    }

    this.flags.setResultsRelatedFlags({
      isSearchFormSubmitted: true,
      isLoading: true,
    });
    this.flags.changeZoomIfPinsNotVisible = true;
    this.flags.checkWholeMapResults = true;
    this.mapService.onSearchFormSubmitted(data);
  }

  /**
   * @description Called when goToSubmittedEvent event was fired, calls the corresponding
   *              method from map service in order to update displayed markers.
   *              Mustn't be called if user submit an empty form.
   */
  public onMapGotoFormSubmitted(data: MapGotoBlockForm): void {
    if (!this.flags.isGoToFormSubmitted && !this.mapService.isGoToFormSubmitted(data)) {
      return;
    }
    this.mapMarker = data;
    this.mediator.onClearMarkerClick();
    this.flags.setResultsRelatedFlags({
      isSearchFormSubmitted: this.mapService.isSearchFormSubmitted(),
      isLoading: true,
      isGoToFormSubmitted: true,
    });
    this.flags.changeZoomIfPinsNotVisible = false;
    this.flags.checkWholeMapResults = false;
    this.mapService.onMapGotoFormSubmitted(data);
  }

  /**
   * @description Called on filterChanged event, updates selected filter and calls
   *              changeMarkers() method to update markers list depend on selected filter.
   * @param { MapFilters } selectedFilter
   */
  public onFilterChanged(selectedFilter: MapFilters): void {
    if (this.mapState?.filter !== selectedFilter) {
      if (selectedFilter === MapFilters.COVID && this.searchBlockComponent) {
        this.searchBlockComponent.onCloseSearchBlock(new Event(''));
      }

      this.flags.setResultsRelatedFlags({
        isSearchFormSubmitted: this.mapService.isSearchFormSubmitted(),
        isLoading: true,
        isGoToFormSubmitted: false,
      });
      this.flags.changeZoomIfPinsNotVisible = false;
      this.flags.checkWholeMapResults = false;
      this.flags.noResultsFoundGlobally = false;

      this.mapService.onFilterChanged(selectedFilter);

      this.isAuthSeeMapLogin(selectedFilter, this.mapState.zoom);
    }
  }

  /**
   * @description Called on changes event, updates currentMapLocation value and calls
   *              changeMarkers() method to update markers list depend on current map
   *              location. Calls toggleButtonVisibility() method to hide container with
   *              'View map' button if the map is initialized.
   * @param { MapState } state
   */
  public onChanges(state: MapState): void {
    const mapChanged: boolean = this.mapService.onMapChanges(state, this.flags.isMapInitialized);
    if (!mapChanged) {
      this.mediator.onClearMarkerClick();
    }

    if (this.flags.isMapInitialized) {
      this.toggleButtonVisibility();
    }

    this.isAuthSeeMapLogin(state.filter, state.zoom);

    this.flags.isMapInitialized = true;

    if (!isInNotAggregationZoomRange(this.mapState.zoom)) {
      this.displayedMarkers = this.filterVisibleMarkers(this.mapMarkers.allMarkers, this.mapState);
    }
  }

  public navLoginPage(): void {
    promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
  }

  public checkMarkersInGeoBoundingBox(): boolean {
    return this.mapMarkers.allMarkers.some((marker: CustomMarker) => this.checkMarkerInCurrentViewport(marker));
  }

  /**
   * @description Subscribes to mapStateChanged event and mapState and properties values.
   */
  private subscribeToMapStateChanges(): void {
    this.subscriptions$.push(
      this.mapService.mapStateChanged.subscribe((state: MapState) => {
        this.mapState = state;
        this.flags.isNotCovidTab = this.mapState.filter !== MapFilters.COVID;
        this.flags.isSearchFormSubmitted = this.mapService.isSearchFormSubmitted();
        this.flags.isGoToFormSubmitted = this.mapService.isGoToFormSubmitted();
      }),
    );

    this.mapService.fetchInitialMapState();
  }

  /**
   * @description Subscribes to onMarkersChanges event and updates markers list.
   */
  private subscribeToMarkersChanges(): void {
    this.subscriptions$.push(
      this.mapService.onMarkersChanges.subscribe((markersStateChanges: MarkersStateChanges) => {
        this.flags.setResultsRelatedFlags({
          isSearchFormSubmitted: this.mapService.isSearchFormSubmitted(),
          isLoading: markersStateChanges.reset,
          isGoToFormSubmitted: this.mapService.isGoToFormSubmitted(),
        });
        this.flags.disableMarkerClick = false;

        this.mapMarkers = new MapMarkers(
          markersStateChanges.markers.allMarkers || [],
          markersStateChanges.markers.areaMarkers || [],
        );

        this.displayedMarkers = isInNotAggregationZoomRange(this.mapState.zoom)
          ? markersStateChanges.markers.allMarkers
          : this.filterVisibleMarkers(markersStateChanges.markers.allMarkers, this.mapState);

        if (this.flags.moveToNearestMarker && this.mapMarkers.allMarkers.length > 0) {
          this.moveToNearestMarker();
        }

        if (this.flags.markerLocationLiteral) {
          if (!this.checkMarkersInGeoBoundingBox()) {
            this.flags.moveToNearestMarker = true;
            this.mapService.searchLocationLiteralMarkers(this.flags.markerLocationLiteral);
          }
          this.flags.markerLocationLiteral = undefined;
          return;
        }

        if (this.mapService.isSearchFormSubmitted() && this.flags.changeZoomIfPinsNotVisible) {
          if (this.checkMarkersInVisibleArea()) {
            this.flags.changeZoomIfPinsNotVisible = false;
            this.flags.checkWholeMapResults = false;
          }
        }

        this.mapFlow.markersWereUpdated();
      }),
    );
  }

  private moveToNearestMarker(): void {
    this.flags.moveToNearestMarker = false;
    const marker: CustomMarker = this.mapMarkers.allMarkers.reduce((prev: CustomMarker, current: CustomMarker) =>
      this.getDistanceFromLocationCenterToMarker(prev) < this.getDistanceFromLocationCenterToMarker(current)
        ? prev
        : current,
    );

    this.mapService.emitMapState({
      ...this.mapState,
      location: {
        ...this.mapState.location,
        center: {
          lat: marker.latitude,
          lon: marker.longitude,
        },
      },
    });
  }

  private getDistanceFromLocationCenterToMarker(marker: CustomMarker): number {
    return DistanceCalculator.getDistanceBetweenTwoMapPoints(this.mapState.location.center, {
      lat: marker.latitude,
      lon: marker.longitude,
    });
  }

  private subscribeOnAggregationRequestErrors(): void {
    this.mapService.aggregationRequestErrorObservable.subscribe(() => {
      this.flags.disableMarkerClick = false;
    });
  }

  private checkMarkersInVisibleArea(): boolean {
    if (this.mapMarkers.allMarkers.length === 0) {
      this.flags.noResultsFoundGlobally = true;
      if (isInNotAggregationZoomRange(this.mapState.zoom) && this.flags.checkWholeMapResults) {
        this.mapService.getSearchResultWithoutLocation();
        this.flags.checkWholeMapResults = false;
        return false;
      }
      return true;
    }

    this.flags.noResultsFoundGlobally = false;
    if (!this.checkMarkersInGeoBoundingBox()) {
      const state: MapState = { ...this.mapState, zoom: MapSettings.MAP_MIN_ZOOM_LEVEL };
      this.mapService.updateCurrentMapState(state);
      this.mapService.emitMapState(state);
    }
    return true;
  }

  private checkMarkerInCurrentViewport(marker: CustomMarker): boolean {
    return (
      this.mapState.location.northWestCoords.lat > marker.latitude &&
      this.mapState.location.southEastCoords.lat < marker.latitude &&
      this.mapState.location.northWestCoords.lon < marker.longitude &&
      this.mapState.location.southEastCoords.lon > marker.longitude
    );
  }

  /**
   * @description Subscribes to searchBlockTabContent event to update searchBlockContent
   *              prop value with incoming data. Initiates searchBlockContent prop with
   *              default form content data.
   */
  private subscribeToSearchBlockTabContentChanges(): void {
    this.subscriptions$.push(
      this.mapService.searchBlockTabContent.subscribe((content: MapSearchBlockTabContent) => {
        this.searchBlockContent.formContent.fieldAttributes = content.fieldAttributes;
        this.searchBlockContent.title = content.title;
      }),
    );

    this.searchBlockContent = this.mapService.searchBlockFormContent;
  }

  private subscribeToMapGotoContentChanges(): void {
    this.subscriptions$.push(
      this.mapService.goToBlockTabContent.subscribe((content: MapGotoBlockTabContent) => {
        this.goToBlockContent.formContent.fieldAttributes = content.fieldAttributes;
        this.goToBlockContent.title = content.title;
      }),
    );

    this.goToBlockContent = this.mapService.goToBlockFormContent;
  }

  /**
   * @description Subscribes to sessionChanges event and updates isLoggedIn value on changes.
   */
  private subscribeToSessionChanges(): void {
    this.subscriptions$.push(
      this.mapService.sessionActivity.subscribe((isLoggedIn: boolean) => {
        this.flags.isLoggedIn = isLoggedIn;
      }),

      this.mapService.sessionAdmin.subscribe((isAdminSession: boolean) => {
        this.isAdminSession = isAdminSession;
      }),
    );
  }

  private subscribeToOnClickItemAction(): void {
    this.subscriptions$.push(
      this.mediator.searchItemClicked.subscribe(() => {
        if (window.innerWidth <= this.searchBlockComponent.elementRect.width) {
          this.flags.isSearchBlockVisible = false;
        }
      }),
    );
  }

  private changeMinZoomScreenSize(): void {
    if (window.innerWidth >= 1500) {
      this.zoomOptions.minZoom = 3;
    } else if (window.innerWidth >= 500) {
      this.zoomOptions.minZoom = 2;
    }
  }

  private isAuthSeeMapLogin(selectedFilter: MapFilters, zoom: number): void {
    this.isAuthSeeMap = true;

    if (
      zoom >= 9 &&
      !this.flags.isLoggedIn &&
      (selectedFilter === MapFilters.ORGANIZATIONS || selectedFilter === MapFilters.PEOPLE)
    ) {
      this.isAuthSeeMap = false;
    }
  }

  private subscribeToRouteChanges(): void {
    this.subscriptions$.push(
      this.route.url.subscribe(() => {
        const entity = this.route.snapshot.params['entity'] as string;
        const id = this.route.snapshot.queryParams['id'] as string;
        this.processRouteParams(entity, id);
      }),
    );
  }

  private processRouteParams(entity: string, id: string): void {
    const provider = MapItemDetailsProviderStrategy.getProvider(entity, this.apiService);

    if (!provider) {
      /**
       * If there is not a provider by the entity name from params, it means that the component doesn't support this entity key.
       * In this case the system redirects to the default page.
       */
      promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
      return;
    }

    this.mapFlow.deactivate();

    if (id) {
      this.mapFlow = new MapSelectItemFlow(this, provider, id);
    } else {
      this.mapFlow = new MapViewFlow(this, provider);
    }

    this.mapFlow.activate();
  }

  private filterVisibleMarkers(allMarkers: CustomMarker[], mapState: MapState): CustomMarker[] {
    if (hasCovidMarkers(allMarkers)) {
      return allMarkers;
    }

    const viewPortOffset: number = calculateOffsetDegree(this.mapState.zoom);
    return allMarkers.filter((marker: CustomMarker) => this.isMarkerVisible(marker, mapState, viewPortOffset));
  }

  private isMarkerVisible(marker: CustomMarker, mapState: MapState, viewPortOffset: number): boolean {
    const topLeft = mapState.location.northWestCoords;
    const bottomRight = mapState.location.southEastCoords;

    const inLat =
      marker.latitude <= topLeft.lat + viewPortOffset && marker.latitude >= bottomRight.lat - viewPortOffset;

    let inLng: boolean;

    if (topLeft.lon <= bottomRight.lon) {
      inLng = marker.longitude >= topLeft.lon - viewPortOffset && marker.longitude <= bottomRight.lon + viewPortOffset;
    } else {
      inLng = marker.longitude >= topLeft.lon - viewPortOffset || marker.longitude <= bottomRight.lon + viewPortOffset;
    }

    return inLat && inLng;
  }
}
